import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

      { title: "Privacy Policy | The Digital Debrief" },
      {
        name: "description",
        content:
          "Privacy Policy for The Digital Debrief, a free newsletter by Scott Cunningham delivered via beehiiv.",
      },
      { property: "og:title", content: "Privacy Policy | The Digital Debrief" },
      {
        property: "og:description",
        content:
          "Privacy Policy for The Digital Debrief, a free newsletter by Scott Cunningham delivered via beehiiv.",
      },
      { property: "og:url", content: "https://scottcbusiness.com/privacy" },
      { name: "twitter:title", content: "Privacy Policy | The Digital Debrief" },
      {
        name: "twitter:description",
        content:
          "Privacy Policy for The Digital Debrief, a free newsletter by Scott Cunningham delivered via beehiiv.",
      },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 py-24 lg:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
        The Digital Debrief
      </p>
      <h1 className="mt-4 font-display text-4xl md:text-5xl">Privacy Policy</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
      </p>

      <div className="mt-10 space-y-8 text-base leading-relaxed">
        <section>
          <h2 className="font-display text-2xl">1. Introduction</h2>
          <p>
            This Privacy Policy explains how Scott Cunningham ("Scott," "we," "us," or "our")
            collects, uses, shares, and protects information when you subscribe to or interact with{" "}
            <strong>The Digital Debrief</strong> (the "Newsletter") and the website at
            scottcbusiness.com (the "Site"). We are based in Toronto, Ontario, Canada and act as the
            controller of your personal information.
          </p>
          <p className="mt-2">
            The Newsletter is a free publication delivered through <strong>beehiiv, Inc.</strong>{" "}
            ("beehiiv"). beehiiv processes data on our behalf as our email service provider; their
            handling of your information is also governed by beehiiv's{" "}
            <a
              href="https://www.beehiiv.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">2. Information We Collect</h2>
          <p>We collect the following categories of information:</p>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            <li>
              <strong>Information you provide.</strong> Email address (required to subscribe), and
              any optional details you submit such as name, location, referral source, or messages
              sent via email or contact forms.
            </li>
            <li>
              <strong>Engagement and technical data (collected automatically by beehiiv).</strong>{" "}
              Email opens, clicks, time of interaction, referrer, IP address, approximate location
              derived from IP, device, browser, and operating system, and similar metadata used to
              deliver and measure the Newsletter.
            </li>
            <li>
              <strong>Site analytics.</strong> Standard log data when you visit the Site (pages
              viewed, referrer, IP-derived approximate location, device and browser info), which we
              may use to understand traffic and improve content.
            </li>
            <li>
              <strong>Communications.</strong> Records of correspondence if you reply to the
              Newsletter or contact us directly.
            </li>
          </ul>
          <p className="mt-2">
            We do <strong>not</strong> collect payment information, financial data, government IDs,
            or sensitive categories of personal information, and we do not run paid subscriptions.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">3. How We Use Information</h2>
          <p>We use the information we collect to:</p>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            <li>Deliver the Newsletter and related transactional messages (e.g. confirmations);</li>
            <li>
              Measure engagement and understand which content resonates so we can improve quality
              and relevance;
            </li>
            <li>Respond to your inquiries and provide support;</li>
            <li>Maintain the security and integrity of the Services and prevent abuse;</li>
            <li>Comply with legal obligations and enforce our Terms.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl">4. Legal Bases (EEA / UK)</h2>
          <p>
            If you are located in the European Economic Area or the United Kingdom, our legal bases
            for processing are: (a) <strong>consent</strong>, which you provide when you subscribe
            and which you can withdraw at any time by unsubscribing; (b) our{" "}
            <strong>legitimate interests</strong> in operating, securing, and improving the
            Newsletter; and (c) <strong>compliance with legal obligations</strong> where applicable.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">5. How We Share Information</h2>
          <p>
            We do <strong>not sell or rent</strong> your personal information. We share limited
            information only as follows:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-6">
            <li>
              <strong>Service providers.</strong> beehiiv (email delivery and analytics) and the
              hosting, infrastructure, and analytics providers that operate the Site, each acting as
              a processor under contract.
            </li>
            <li>
              <strong>Legal and safety.</strong> When required by law, subpoena, court order, or
              government request, or to protect rights, property, or safety.
            </li>
            <li>
              <strong>Business transfers.</strong> In connection with a merger, acquisition,
              financing, or sale of assets, subject to standard confidentiality protections.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl">6. Cookies, Pixels, and Tracking</h2>
          <p>
            Newsletter emails include tracking pixels and tracked links, used by beehiiv to record
            opens and clicks. The Site may use minimal first-party and third-party cookies for
            functionality and aggregate analytics. You can disable cookies in your browser and
            disable image loading in your email client to limit tracking.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">7. International Data Transfers</h2>
          <p>
            We are based in Canada and our service providers (including beehiiv) are based in the
            United States and other jurisdictions. By subscribing, you understand that your
            information may be transferred to, stored, and processed in countries with
            data-protection laws that differ from those in your country. Where required, our
            providers rely on appropriate safeguards (such as Standard Contractual Clauses) for
            international transfers.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">8. Data Retention</h2>
          <p>
            We retain your subscriber information for as long as you remain subscribed and for a
            reasonable period afterward to comply with legal obligations, resolve disputes, and
            enforce our agreements. If you unsubscribe, we may retain a hashed or suppression record
            of your email address to ensure we do not contact you again. Engagement data may be
            retained in aggregated or anonymized form.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">9. Security</h2>
          <p>
            We use commercially reasonable administrative, technical, and physical safeguards
            designed to protect your information. No method of transmission or storage is 100%
            secure, however, and we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">10. Your Rights and Choices</h2>
          <p>
            Depending on your location, you may have the right to: access, correct, update, delete,
            or port your personal information; object to or restrict certain processing; withdraw
            consent; and lodge a complaint with your local data-protection authority. To exercise
            any of these rights, contact us using the details below. You can also unsubscribe at any
            time using the link in any email.
          </p>
          <p className="mt-2">
            <strong>California residents:</strong> we do not sell or share personal information as
            defined under the CCPA/CPRA. You may request access to or deletion of your personal
            information by contacting us.
          </p>
          <p className="mt-2">
            <strong>Canadian residents:</strong> we comply with applicable Canadian privacy laws,
            including PIPEDA and Quebec's Law 25.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">11. Children's Privacy</h2>
          <p>
            The Services are not directed to children under 16, and we do not knowingly collect
            personal information from children under 16. If you believe a child has provided us with
            personal information, please contact us and we will delete it.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">12. Third-Party Links</h2>
          <p>
            The Newsletter and Site may contain links to third-party websites and services. We are
            not responsible for the privacy practices or content of those third parties; please
            review their privacy policies before providing them with information.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">13. Do Not Track</h2>
          <p>
            We do not currently respond to browser "Do Not Track" signals because no consistent
            industry standard exists. You can control tracking through cookie and email-client
            settings as described above.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">14. Changes to This Policy</h2>
          <p>
            We may update this Policy from time to time. The "Last updated" date above reflects the
            most recent revision. Material changes will be communicated via the Newsletter or posted
            on this page.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl">15. Contact</h2>
          <p>
            Privacy questions or requests? Email{" "}
            <a href="mailto:scottcbusiness@protonmail.com" className="underline">
              scottcbusiness@protonmail.com
            </a>
            . You can also review our{" "}
            <a href="/terms" className="underline">
              Terms of Service
            </a>
            .
          </p>
        </section>
      </div>
    </article>
  );
}
